<script setup lang="ts">
import { useCookieConsentStore, type CookiePreferences } from '~/stores/cookieConsent'

const cookieStore = useCookieConsentStore()
const preferences = ref<CookiePreferences>({ ...cookieStore.preferences })

// Add this computed for template use
const preferencesValue = computed(() => preferences.value)

// Modal controller for detailed preferences
const preferencesModalController = useModal()

// Initialize preferences from store
onMounted(() => {
  preferences.value = { ...cookieStore.preferences }
})

// Handle preference changes
const updatePreference = (service: keyof CookiePreferences, value: boolean) => {
  if (service === 'necessary') return // Cannot disable necessary cookies
  preferences.value[service] = value
  cookieStore.updatePreferences({ [service]: value })
}

// Handle checkbox change events
const handleCheckboxChange = (event: Event, service: keyof CookiePreferences) => {
  const target = event.target as HTMLInputElement
  if (target) {
    updatePreference(service, target.checked)
  }
}

// Handle accept all
const handleAcceptAll = () => {
  cookieStore.acceptAll()
  preferencesModalController.close()
}

// Handle accept selected
const handleAcceptSelected = () => {
  cookieStore.updatePreferences({ ...preferences.value })
  cookieStore.acceptSelected()
  preferencesModalController.close()
}

// Handle accept only technically necessary
const handleAcceptOnlyNecessary = () => {
  cookieStore.acceptOnlyNecessary()
}

// Open preferences modal
// const openPreferences = () => {
//   preferencesModalController.open()
// }

const serviceList: { key: keyof CookiePreferences }[] = [
  { key: 'googleAnalytics' },
  { key: 'googleMaps' },
  { key: 'youtube' },
  { key: 'microsoftBing' },
  { key: 'googleTagManager' },
]

import CookiePreferencesModal from '~/components/CookiePreferencesModal.vue'
const cookieModalRef = ref()

const openPreferences = () => {
  cookieModalRef.value?.open()
}
</script>

<template>
  <Transition
    enter-active-class="transition-all duration-300 ease-out"
    enter-from-class="opacity-0 translate-y-full"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition-all duration-300 ease-in"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 translate-y-full"
  >
    <div
      v-if="cookieStore.isVisible"
      class="fixed bottom-0 left-0 right-0 z-[999] bg-white border-t border-gray-200 shadow-lg"
    >
      <div class="container mx-auto px-4 py-6 max-w-6xl">
        <!-- Main Banner -->
        <div class="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div class="flex-1">
            <h3 class="text-lg font-medium text-gray-900 mb-2">
              {{ $t('cookieConsent.title') }}
            </h3>
            <p class="text-sm text-gray-600 leading-relaxed">
              {{ $t('cookieConsent.description') }}
              <span v-if="false"><!-- legacy learn more button --></span>
            </p>
          </div>
        </div>
        <div class="flex flex-col mt-4 sm:flex-row gap-3 w-full lg:w-auto">
          <button
            @click="openPreferences"
            class="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
          >
            {{ $t('cookieConsent.individualSettings') }}
          </button>
          <button
            @click="handleAcceptOnlyNecessary"
            class="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
          >
            {{ $t('cookieConsent.acceptOnlyNecessary') }}
          </button>
          <button
            @click="handleAcceptAll"
            class="px-6 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded-full transition-colors"
          >
            {{ $t('cookieConsent.selectAll') }}
          </button>
        </div>
      </div>
    </div>
  </Transition>

  <CookiePreferencesModal ref="cookieModalRef" />
</template> 